import { h } from '../lib/dom.js';
import { getState } from '../store.js';
import {
  closeStatusPanel, openLicense, toggleStatusInfo,
  retestOnline, retestBlockchains, retestFxRates
} from '../actions.js';
import { APP_COMMIT, BUILD_DATE, APP_UPTIME } from '../version.js';
import { iconMarkup } from '../icons/index.js';
import { t, getLanguage } from '../i18n/index.js';

// Secure means online *and* the page itself was served over HTTPS (or is
// running from localhost in dev) — an online-but-plain-HTTP load shouldn't
// read as "secure".
function isSecureContext() {
  if (typeof location === 'undefined') return true;
  return location.protocol === 'https:' || location.hostname === 'localhost';
}

// Intl.RelativeTimeFormat (built-in, no new dependency) instead of
// hand-rolled "N mins/hours/days ago" strings — English pluralization
// rules ("1 min" vs "2 mins") don't generalize to every language's plural
// system, but the browser's own CLDR data already knows each locale's
// rules and grammar, keyed off the same language code driving every t()
// call elsewhere in this file.
function relativeTime(ms) {
  const diffMs = ms - Date.now();
  const mins = Math.round(diffMs / 60000);
  const rtf = new Intl.RelativeTimeFormat(getLanguage(), { numeric: 'auto' });
  if (Math.abs(mins) < 60) return rtf.format(mins, 'minute');
  const hours = Math.round(diffMs / 3600000);
  if (Math.abs(hours) < 24) return rtf.format(hours, 'hour');
  const days = Math.round(diffMs / 86400000);
  return rtf.format(days, 'day');
}

// Function, not a module-level object — evaluated fresh on every render
// (from renderStatusPanel below) so a language switch's re-render sees the
// current catalog instead of freezing whatever was active at import time.
function explainers() {
  return {
    online: t('modals.status.explainerOnline'),
    secure: t('modals.status.explainerSecure'),
    blockchains: t('modals.status.explainerBlockchains'),
    fxRates: t('modals.status.explainerFxRates'),
    uptime: t('modals.status.explainerUptime'),
    mode: t('modals.status.explainerMode'),
    version: t('modals.status.explainerVersion')
  };
}

// One row: label + info toggle on top, value + status icon + optional
// retest button below, with the explainer text expanding underneath when
// toggled open (mirrors Home's showBalanceInfo/showActivityInfo pattern).
function statusRow({ label, value, valueClass, icon, iconClass, extraIcon, infoKey, onRetest }) {
  const s = getState();
  return h('div', { class: 'status-row' }, [
    h('div', { class: 'status-row-top' }, [
      h('div', { class: 'status-row-label' }, [
        icon ? h('span', { class: 'inline-icon' + (iconClass ? ' status-' + iconClass : ''), html: iconMarkup(icon, { size: 14 }) }) : null,
        h('span', { class: 'k' }, label),
        h('button', { class: 'icon-btn-plain', 'aria-label': t('modals.status.whatIsThis'), onclick: () => toggleStatusInfo(infoKey) },
          h('span', { html: iconMarkup('circleQuestionMark', { size: 12, color: '#757993' }) }))
      ]),
      h('div', { class: 'status-row-value' }, [
        h('span', { class: valueClass ? 'v ' + valueClass : 'v' }, value),
        extraIcon ? h('span', { class: 'inline-icon status-warn', html: iconMarkup(extraIcon, { size: 14 }) }) : null,
        onRetest ? h('button', { class: 'icon-btn-plain', 'aria-label': t('modals.status.retest'), onclick: onRetest },
          h('span', { html: iconMarkup('rotateCcw', { size: 13 }) })) : null
      ])
    ]),
    s.statusInfoRow === infoKey ? h('div', { class: 'text-sm text-muted' }, explainers()[infoKey]) : null
  ]);
}

export function renderStatusPanel() {
  const s = getState();
  if (!s.showStatusPanel) return null;

  const secure = isSecureContext();

  const ethOk = s.chainConnectivity['Ethereum · ERC-20'];
  const tronOk = s.chainConnectivity['Tron · TRC-20'];
  const chainsChecking = ethOk === null || tronOk === null;
  const chainsAllOk = ethOk === true && tronOk === true;
  const chainsAllDown = ethOk === false && tronOk === false;
  const blockchainsLabel = chainsChecking ? t('modals.status.checking') : chainsAllOk ? t('modals.status.connected') : chainsAllDown ? t('modals.status.unreachable') : t('modals.status.degraded');
  const blockchainsIcon = chainsChecking || chainsAllOk ? 'shieldCheck' : 'shieldX';
  const blockchainsIconClass = chainsChecking ? undefined : chainsAllOk ? 'good' : 'warn';
  const blockchainsValueClass = chainsChecking ? undefined : chainsAllOk ? 'status-good' : 'status-warn';

  const fx = s.fxRates;
  const fxStale = !fx || (Date.now() - fx.fetchedAt) > 24 * 60 * 60 * 1000;
  const fxLabel = fx ? relativeTime(fx.fetchedAt) : t('modals.status.neverFetched');

  const uptimeValue = parseFloat(APP_UPTIME);
  const uptimeLow = Number.isFinite(uptimeValue) && uptimeValue < 99.99;

  const versionLabel = `${BUILD_DATE}#${APP_COMMIT}`;

  return h('div', { class: 'modal-scrim' }, [
    h('div', { class: 'modal-card' }, [
      h('div', { class: 'modal-title' }, t('modals.status.title')),
      h('div', { class: 'stack' }, [
        statusRow({
          label: t('modals.status.labelOnline'), value: s.online ? t('modals.status.online') : t('modals.status.offline'),
          valueClass: s.online ? 'status-good' : 'status-warn',
          icon: s.online ? 'shieldCheck' : 'shieldOff', iconClass: s.online ? 'good' : 'warn',
          infoKey: 'online', onRetest: retestOnline
        }),
        statusRow({
          label: t('modals.status.labelSecure'), value: secure ? t('modals.status.secure') : t('modals.status.notSecure'),
          valueClass: secure ? 'status-good' : 'status-bad',
          icon: secure ? 'shieldCheck' : 'shieldOff', iconClass: secure ? 'good' : 'bad',
          infoKey: 'secure'
        }),
        statusRow({
          label: t('modals.status.labelBlockchains'), value: blockchainsLabel, valueClass: blockchainsValueClass,
          icon: blockchainsIcon, iconClass: blockchainsIconClass,
          infoKey: 'blockchains', onRetest: retestBlockchains
        }),
        statusRow({
          label: t('modals.status.labelFxRates'), value: fxLabel,
          valueClass: fxStale ? 'status-warn' : 'status-good',
          icon: fxStale ? 'shieldQuestionMark' : 'shieldCheck', iconClass: fxStale ? 'warn' : 'good',
          infoKey: 'fxRates', onRetest: retestFxRates
        }),
        statusRow({
          label: t('modals.status.labelUptime'), value: APP_UPTIME,
          valueClass: uptimeLow ? 'status-warn' : 'status-good',
          icon: uptimeLow ? 'shieldEllipsis' : 'shieldCheck', iconClass: uptimeLow ? 'warn' : 'good',
          infoKey: 'uptime'
        }),
        // Icon-only override for the testnet case (see DECISIONS.md — the
        // header badge this was meant to "match" turned out, on inspection,
        // to be wired to online/offline, not testnet/mainnet; the literal
        // instruction — shieldCog on testnet — is followed on its own
        // merits). The pre-existing mainnet-is-flagged text color is left
        // exactly as it was; the icon deliberately doesn't add a second,
        // contradicting green/red signal on top of it.
        statusRow({
          label: t('modals.status.labelMode'), value: s.testnetEnabled ? t('modals.status.testnet') : t('modals.status.mainnet'),
          valueClass: s.testnetEnabled ? undefined : 'status-bad',
          icon: s.testnetEnabled ? 'shieldCog' : 'shieldCheck',
          infoKey: 'mode'
        }),
        statusRow({
          label: t('modals.status.labelVersion'), value: versionLabel,
          valueClass: s.swUpdateAvailable ? 'status-warn' : 'status-good',
          icon: s.swUpdateAvailable ? 'shieldMinus' : 'shieldCheck', iconClass: s.swUpdateAvailable ? 'warn' : 'good',
          extraIcon: s.swUpdateAvailable ? 'circleFadingArrowUp' : null,
          infoKey: 'version'
        }),
        h('div', { class: 'review-row' }, [
          h('span', { class: 'k' }, t('modals.status.license')),
          h('a', {
            class: 'v provider-link', href: '#',
            onclick: (e) => { e.preventDefault(); openLicense(); }
          }, [
            'MIT ',
            h('span', { class: 'inline-icon', html: iconMarkup('squareArrowOutUpRight', { size: 12 }) })
          ])
        ])
      ]),
      h('button', { class: 'btn btn-secondary', onclick: closeStatusPanel }, t('common.close'))
    ])
  ]);
}
